import { useState } from 'react';
import axios from 'axios';
import { Loader2, AlertTriangle, Download } from 'lucide-react';
import { getApiBase } from './apiBase';
import { downloadCsv } from './AiCopilot/downloadCsv';

const API = axios.create({ baseURL: getApiBase(), withCredentials: true, timeout: 300000 });

const RECIPES = [
  { id: 'hcm-hdl-load-errors', label: 'HDL errors (summary)' },
  { id: 'hcm-hdl-load-errors-detail', label: 'HDL errors (full detail)' },
  { id: 'hcm-hdl-load-errors-all', label: 'HDL load errors (all messages)' },
  { id: 'hcm-hdl-dat-line-errors', label: 'HDL DAT line errors' },
];

export default function HdlErrorsDashboard() {
  const [recipeId, setRecipeId] = useState('hcm-hdl-load-errors');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [selected, setSelected] = useState(null);

  async function runPreview() {
    setLoading(true);
    setError('');
    setSelected(null);
    try {
      const lib = await API.get(`/api/copilot/sql-library/${recipeId}`, { validateStatus: () => true });
      if (lib.status !== 200 || !lib.data.sql) {
        setError(lib.data?.error || 'Recipe not found');
        return;
      }
      const run = await API.post(
        '/api/copilot/run-oracle-sql',
        { sql: lib.data.sql, rowLimit: 500 },
        { validateStatus: () => true }
      );
      if (run.status !== 200 || run.data.ok === false) {
        setError(run.data?.error || 'Preview failed');
        setRows([]);
        return;
      }
      const data = run.data;
      const cols =
        (data.columns || []).map((c) => (typeof c === 'string' ? c : c?.name)).filter(Boolean) ||
        Object.keys(data.rows?.[0] || {});
      setColumns(cols);
      setRows(data.rows || []);
    } catch (e) {
      setError(e.message || 'Failed');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  function pickRow(row) {
    setSelected(row);
  }

  function lineKey(row) {
    return (
      row.LINE_USER_KEY ||
      row.line_user_key ||
      row.Line_User_Key ||
      row.LINE_USER_KEY_VALUE ||
      '—'
    );
  }

  function fileName(row) {
    return row.DATA_FILE_NAME || row.data_file_name || row.Data_File_Name || '—';
  }

  function exportRows() {
    if (!rows.length) return;
    downloadCsv(`hdl-errors-${recipeId}-${Date.now()}.csv`, columns, rows);
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <header>
        <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
          <AlertTriangle className="h-6 w-6 text-amber-600" />
          HDL error dashboard
        </h2>
      </header>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-4">
        <select
          value={recipeId}
          onChange={(e) => setRecipeId(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          {RECIPES.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={runPreview}
          disabled={loading}
          className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
        >
          {loading ? <Loader2 className="inline h-4 w-4 animate-spin" /> : null}
          Run preview
        </button>
        {rows.length > 0 && (
          <button type="button" onClick={exportRows} className="inline-flex items-center gap-1 text-sm text-slate-700">
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        )}
        {rows.length > 0 && <span className="text-xs text-slate-500">{rows.length} rows</span>}
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div>
      )}

      {selected && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
          <div className="font-semibold">Error row detail</div>
          <p>
            <strong>line_user_key:</strong> {lineKey(selected)}
          </p>
          <p>
            <strong>data_file_name:</strong> {fileName(selected)}
          </p>
          <button type="button" onClick={() => setSelected(null)} className="mt-2 text-xs text-amber-800 underline">
            Close
          </button>
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-auto max-h-[28rem] rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50">
              <tr>
                {columns.slice(0, 12).map((c) => (
                  <th key={c} className="px-2 py-1.5 text-left font-semibold text-slate-600">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={i}
                  onClick={() => pickRow(row)}
                  className="cursor-pointer border-t border-slate-100 odd:bg-white even:bg-slate-50 hover:bg-amber-50"
                >
                  {columns.slice(0, 12).map((c) => (
                    <td key={c} className="max-w-[10rem] truncate px-2 py-1 text-slate-700">
                      {row[c] == null ? '' : String(row[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
